import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { initWebPush } from './services/webPushService';
import './App.css';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import DashboardHeader from './components/layout/DashboardHeader';
import SpDashboardHeader from './components/layout/SpDashboardHeader';
import FloatingRiaWidget from './components/common/FloatingRiaWidget';

// Pages
import HomePage from './pages/HomePage';
import UsersPage from './pages/UsersPage';
import PropertiesPage from './pages/PropertiesPage';
import FavoritesPage from './pages/FavoritesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import CmaReportPage from './pages/CmaReportPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AgentVerificationPage from './pages/AgentVerificationPage';
import BrokerVerificationPage from './pages/BrokerVerificationPage';
import BrokerageRegistrationPage from './pages/BrokerageRegistrationPage';
import TCRegistrationScreen from './pages/TCRegistrationScreen';
import InvitePage from './pages/InvitePage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import ConversationPage from './pages/ConversationPage';
import TransactionsPage from './pages/TransactionsPage';
import TransactionDetailPage from './pages/TransactionDetailPage';
import DocumentsPage from './pages/DocumentsPage';
import ListingTransactionPage from './pages/ListingTransactionPage';
import PurchaseTransactionPage from './pages/PurchaseTransactionPage';
import InviteSomeonePage from './pages/InviteSomeonePage';
import InviteInspectorPage from './pages/InviteInspectorPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SmsConsentPage from './pages/SmsConsentPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import NotificationsPage from './pages/NotificationsPage';
import MyNotesPage from './pages/MyNotesPage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentMobileReturnPage from './pages/PaymentMobileReturnPage';
import ConnectedServicesPage from './pages/ConnectedServicesPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import SettingsPage from './pages/SettingsPage';
import ActivityLogPage from './pages/ActivityLogPage';
import UserActivityPage from './pages/UserActivityPage';
import DocusignCallbackPage from './pages/DocusignCallbackPage';
import MailboxCallbackPage from './pages/MailboxCallbackPage';
import DocusignSenderCallbackPage from './pages/DocusignSenderCallbackPage';
import RequestAccessPage from './pages/RequestAccessPage';
import TourPage from './pages/TourPage';
import MFAEnrollmentPage from './pages/MFAEnrollmentPage';
import SpDashboardPage from './pages/SpDashboardPage';
import SpConversationsPage from './pages/SpConversationsPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import CalendarPage from './pages/CalendarPage';
import AgentCalendarPage from './pages/AgentCalendarPage';
import CrmPage from './pages/CrmPage';
import { CRM_ENABLED } from './utils/crm';
import CreateServiceRequestPage from './pages/CreateServiceRequestPage';
import SpProfilePage from './pages/SpProfilePage';
import EmbedFormEditPage from './pages/EmbedFormEditPage';
import SharedDocumentDownloadPage from './pages/SharedDocumentDownloadPage';
import DisclosureRecipientPage from './pages/DisclosureRecipientPage';
import DisclosureViewPage from './pages/DisclosureViewPage';
import CoverSheetPreviewPage from './pages/CoverSheetPreviewPage';
import OfferUploadRecipientPage from './pages/OfferUploadRecipientPage';
import OfferUploadPage from './pages/OfferUploadPage';
import PageViewTracker from './analytics/usePageViews';
import IdentifyUser from './analytics/IdentifyUser';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Guards app-only routes (dashboard, transactions, conversations, property detail, etc.)
// so an unauthenticated visitor hitting any of them directly is sent to the home
// landing page, matching the existing /dashboard behavior.
function RequireAuth({ children }) {
  const isLoggedIn = !!localStorage.getItem('firebaseToken') && localStorage.getItem('isAuthenticated') === 'true';
  if (!isLoggedIn) return <Navigate to="/" replace />;
  return children;
}

// Catches paths that don't match any route (e.g. "/transaction/" with no id) —
// previously these rendered a blank page. Send logged-out visitors home and
// logged-in visitors to their dashboard.
function NotFoundRedirect() {
  const isLoggedIn = !!localStorage.getItem('firebaseToken') && localStorage.getItem('isAuthenticated') === 'true';
  return <Navigate to={isLoggedIn ? '/dashboard' : '/'} replace />;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const inactivityTimer = useRef(null);
  const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes

  useEffect(() => {
    const publicPaths = ['/', '/login', '/register', '/invite', '/sp-invite', '/forgot-password', '/privacy', '/terms', '/sms-consent', '/tour', '/request-access'];
    const isPublicPage = publicPaths.includes(location.pathname);
    const isLoggedIn = !!localStorage.getItem('firebaseToken');

    if (isPublicPage || !isLoggedIn) return;

    const handleSessionExpired = async () => {
      try {
        const auth = getAuth();
        await auth.signOut();
      } catch (e) {
        console.error('Error signing out:', e);
      }
      localStorage.removeItem('user');
      localStorage.removeItem('firebaseToken');
      navigate('/login');
    };

    const resetTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(handleSessionExpired, INACTIVITY_LIMIT);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer.current);
      activityEvents.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [location.pathname, navigate]);

  // Bootstrap FCM web push once per session for returning users. LoginPage
  // fires initWebPush on fresh login, but a returning user with a live
  // firebaseToken never goes through LoginPage — this covers that path.
  // Fire-and-forget: initWebPush silently no-ops on every failure mode.
  const webPushInitAttempted = useRef(false);
  useEffect(() => {
    if (webPushInitAttempted.current) return;
    const token = localStorage.getItem('firebaseToken');
    const authed = localStorage.getItem('isAuthenticated') === 'true';
    if (token && authed) {
      webPushInitAttempted.current = true;
      initWebPush(token);
    }
  }, [location.pathname]);

  // Fix malformed invite URLs: /?invite?role=buyer&... → /invite?role=buyer&...
  useEffect(() => {
    if (location.pathname === '/' && location.search.startsWith('?invite?')) {
      const fixedParams = location.search.replace('?invite?', '');
      navigate(`/invite?${fixedParams}`, { replace: true });
      return;
    }
    // Redirect authenticated users away from home page to dashboard
    if (location.pathname === '/') {
      const isLoggedIn = !!localStorage.getItem('firebaseToken') && !!localStorage.getItem('isAuthenticated');
      if (isLoggedIn) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [location, navigate]);
  const isHomePage = location.pathname === '/';
  const isDashboardPage = location.pathname === '/dashboard';
  const isProfilePage = location.pathname === '/profile';
  const isConversationPage = location.pathname === '/conversations';
  const isTransactionsPage = location.pathname === '/transactions';
  const isMyNotesPage = location.pathname === '/my-notes';
  const isCrmPage = location.pathname === '/crm';
  const isDocumentsPage = location.pathname === '/documents';
  const isPropertiesPage = location.pathname === '/properties';
  const isFavoritesPage = location.pathname === '/favorites';
  const isListingTransactionPage = location.pathname === '/listing-transaction';
  const isPurchaseTransactionPage = location.pathname === '/purchase-transaction';
  const isInviteSomeonePage = location.pathname === '/invite-someone';
  const isTransactionDetailPage = location.pathname.startsWith('/transaction/');
  const isPropertyDetailPage = location.pathname.match(/^\/transaction\/[^/]+\/property\//) || location.pathname.match(/^\/property\/[^/]+$/);
  const isInvitePage = location.pathname === '/invite' || location.pathname === '/sp-invite';
  const isNotificationsPage = location.pathname === '/notifications';
  const isPaymentsPage = location.pathname === '/payments';
  // HTTPS intermediate for Stripe hosted Checkout → mobile ria:// deep-link.
  // No auth, no chrome — full-screen hop that redirects to ria:// on mount.
  const isPaymentMobileReturnPage = location.pathname.startsWith('/payment/mobile-');
  const isConnectedServicesPage = location.pathname === '/connected-services';
  const isNotificationSettingsPage = location.pathname === '/notification-settings';
  const isSettingsPage = location.pathname === '/settings';
  const isActivityLogPage = location.pathname === '/activity-log';
  const isUserActivityPage = location.pathname === '/user-activity';
  // The mailbox callback is a bare OAuth landing page like DocuSign's: no header, footer or chat widget.
  const isDocusignCallbackPage = location.pathname === '/auth/callback' || location.pathname === '/auth/mailbox/callback';
  const isDocusignSenderCallbackPage = location.pathname === '/docusign-sender-callback';
  const isTourPage = location.pathname === '/tour';
  const isMFAEnrollmentPage = location.pathname === '/mfa-enrollment';
  const isSpDashboardPage = location.pathname === '/dashboard/sp';
  const isSpConversationsPage = location.pathname === '/conversations/sp';
  const isJobsPage = location.pathname === '/jobs';
  const isJobDetailPage = location.pathname.startsWith('/job/');
  const isCalendarPage = location.pathname === '/calendar';
  const isSpCalendarPage = location.pathname === '/calendar/sp';
  const isCreateServiceRequestPage = location.pathname === '/create-service-request';
  const isSpProfilePage = location.pathname === '/profile/sp';
  const isInviteInspectorPage = location.pathname === '/invite-inspector';
  // Standalone editor embedded in the mobile app's WebView — full-screen, no chrome, no
  // normal auth session (see EmbedFormEditPage.js / App.js route comment above).
  const isEmbedFormEditPage = location.pathname === '/embed/forms/edit';
  // Public share-download landing page — routed from the "Download Document"
  // email link. No auth, no chrome, no RiaWidget: page auto-fetches the file
  // from the API and triggers a Blob download.
  const isShareDownloadPage = location.pathname.startsWith('/share/');
  // Folder-share recipient viewer sits on /shared/ instead of /disclosure/ so
  // the URL a Transaction Documents folder recipient sees never says
  // "disclosure". Same components mount on both routes — the wording is keyed
  // off metadata.folder, not the path.
  const isDisclosurePublicPage = location.pathname.startsWith('/disclosure/')
      || location.pathname.startsWith('/shared/');
  // Public offer-upload landing + upload pages — same shell/no-chrome treatment
  // as the disclosure recipient flow. Reuses the same suppressor so Header /
  // DashboardHeader / Footer / RiaWidget all stay off.
  const isOfferUploadPublicPage = location.pathname.startsWith('/offer/');

  // Show header on all pages except home page, dashboard page, profile page, conversation page, transactions page, documents page, properties page, listing transaction page, purchase transaction page, transaction detail page, property detail page, invite page, notifications page, and my notes page
  const shouldShowHeader = !isHomePage && !isDashboardPage && !isProfilePage && !isConversationPage && !isTransactionsPage && !isDocumentsPage && !isPropertiesPage && !isFavoritesPage && !isListingTransactionPage && !isPurchaseTransactionPage && !isInviteSomeonePage && !isTransactionDetailPage && !isPropertyDetailPage && !isInvitePage && !isNotificationsPage && !isPaymentsPage && !isConnectedServicesPage && !isNotificationSettingsPage && !isSettingsPage && !isActivityLogPage && !isUserActivityPage && !isDocusignCallbackPage && !isDocusignSenderCallbackPage && !isTourPage && !isMFAEnrollmentPage && !isSpDashboardPage && !isSpConversationsPage && !isJobsPage && !isJobDetailPage && !isCalendarPage && !isSpCalendarPage && !isCreateServiceRequestPage && !isSpProfilePage && !isInviteInspectorPage && !isEmbedFormEditPage && !isShareDownloadPage && !isDisclosurePublicPage && !isOfferUploadPublicPage && !isMyNotesPage && !isCrmPage && !isPaymentMobileReturnPage;
  // Default header — every dashboard-style page except the Service Provider's
  // own 5 pages, which get their own isolated header component below.
  const shouldShowDashboardHeader = isDashboardPage || isProfilePage || isConversationPage || isTransactionsPage || isDocumentsPage || isPropertiesPage || isFavoritesPage || isListingTransactionPage || isPurchaseTransactionPage || isInviteSomeonePage || isTransactionDetailPage || isPropertyDetailPage || isNotificationsPage || isPaymentsPage || isConnectedServicesPage || isNotificationSettingsPage || isSettingsPage || isActivityLogPage || isUserActivityPage || isCalendarPage || isCreateServiceRequestPage || isMyNotesPage || isCrmPage;
  // Service Provider header — a separate component/stylesheet (see
  // SpDashboardHeader.js) so its nav width/menu layout, tuned for the SP's
  // own 4-tab nav, can't affect (or be affected by) the header other roles use.
  const shouldShowSpDashboardHeader = isSpDashboardPage || isSpConversationsPage || isJobsPage || isJobDetailPage || isSpCalendarPage || isSpProfilePage || isInviteInspectorPage;
  // Floating "Ask Ria" bubble — shown on every page after login, for any
  // user role. Excludes the pre-login/public pages, transitional
  // auth/callback pages where there's no logged-in app experience yet, and
  // the Dashboard page (which already has its own inline Ask Ria card).
  const riaWidgetExcludedPaths = ['/', '/login', '/register', '/invite', '/sp-invite', '/forgot-password', '/privacy', '/terms', '/sms-consent', '/tour', '/request-access', '/auth/callback', '/auth/mailbox/callback', '/docusign-sender-callback', '/mfa-enrollment', '/embed/forms/edit'];
  const isLoggedIn = !!localStorage.getItem('firebaseToken');
  const shouldShowRiaWidget = isLoggedIn && !isDashboardPage && !isShareDownloadPage && !isDisclosurePublicPage && !isOfferUploadPublicPage && !isPaymentMobileReturnPage && !riaWidgetExcludedPaths.includes(location.pathname);

  return (
    <div className="App">
      <ScrollToTop />
      <PageViewTracker />
      <IdentifyUser />
      {shouldShowHeader && <Header />}
      {shouldShowDashboardHeader && <DashboardHeader />}
      {shouldShowSpDashboardHeader && <SpDashboardHeader />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/properties" element={<RequireAuth><PropertiesPage /></RequireAuth>} />
            <Route path="/favorites" element={<RequireAuth><FavoritesPage /></RequireAuth>} />
            <Route path="/property/:id" element={<RequireAuth><PropertyDetailPage /></RequireAuth>} />
            <Route path="/cma/:reportId" element={<RequireAuth><CmaReportPage /></RequireAuth>} />
            <Route path="/transaction/:transactionId/property/:propertyId" element={<RequireAuth><PropertyDetailPage /></RequireAuth>} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/invite" element={<InvitePage />} />
            {/* Vendor-app invite path — same InvitePage component handles both;
                it reads window.location.pathname to pick the right static
                landing (invite.html vs sp-invite.html) on mobile redirect. */}
            <Route path="/sp-invite" element={<InvitePage />} />
            <Route path="/agent-verification" element={<AgentVerificationPage />} />
            <Route path="/broker-verification" element={<BrokerVerificationPage />} />
            <Route path="/brokerage-registration" element={<BrokerageRegistrationPage />} />
            <Route path="/tc-registration" element={<TCRegistrationScreen />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
            <Route path="/conversations" element={<RequireAuth><ConversationPage /></RequireAuth>} />
            <Route path="/transactions" element={<RequireAuth><TransactionsPage /></RequireAuth>} />
            <Route path="/transaction/:id" element={<RequireAuth><TransactionDetailPage /></RequireAuth>} />
            <Route path="/transaction/:id/disclosure/cover-sheet-preview" element={<RequireAuth><CoverSheetPreviewPage /></RequireAuth>} />
            <Route path="/documents" element={<RequireAuth><DocumentsPage /></RequireAuth>} />
            <Route path="/listing-transaction" element={<RequireAuth><ListingTransactionPage /></RequireAuth>} />
            <Route path="/purchase-transaction" element={<RequireAuth><PurchaseTransactionPage /></RequireAuth>} />
            <Route path="/invite-someone" element={<RequireAuth><InviteSomeonePage /></RequireAuth>} />
            <Route path="/invite-inspector" element={<RequireAuth><InviteInspectorPage /></RequireAuth>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-consent" element={<SmsConsentPage />} />
            <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
            <Route path="/my-notes" element={<RequireAuth><MyNotesPage /></RequireAuth>} />
            <Route path="/payments" element={<RequireAuth><PaymentsPage /></RequireAuth>} />
            {/* HTTPS intermediate for Stripe hosted Checkout → mobile. No auth,
                no chrome; just reads outcome + session_id from the URL and
                redirects to the ria:// deep-link so iOS Safari / Android
                Chrome reliably hand off to the Rialty mobile app. */}
            <Route path="/payment/mobile-return" element={<PaymentMobileReturnPage />} />
            <Route path="/payment/mobile-cancel" element={<PaymentMobileReturnPage />} />
            <Route path="/connected-services" element={<RequireAuth><ConnectedServicesPage /></RequireAuth>} />
            <Route path="/notification-settings" element={<RequireAuth><NotificationSettingsPage /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
            <Route path="/auth/callback" element={<DocusignCallbackPage />} />
            <Route path="/auth/mailbox/callback" element={<MailboxCallbackPage />} />
            <Route path="/docusign-sender-callback" element={<DocusignSenderCallbackPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="/activity-log" element={<RequireAuth><ActivityLogPage /></RequireAuth>} />
            <Route path="/user-activity" element={<RequireAuth><UserActivityPage /></RequireAuth>} />
            <Route path="/tour" element={<TourPage />} />
            <Route path="/mfa-enrollment" element={<RequireAuth><MFAEnrollmentPage /></RequireAuth>} />
            <Route path="/dashboard/sp" element={<RequireAuth><SpDashboardPage /></RequireAuth>} />
            <Route path="/conversations/sp" element={<RequireAuth><SpConversationsPage /></RequireAuth>} />
            <Route path="/jobs" element={<RequireAuth><JobsPage /></RequireAuth>} />
            <Route path="/job/:jobId" element={<RequireAuth><JobDetailPage /></RequireAuth>} />
            <Route path="/calendar" element={<RequireAuth><AgentCalendarPage /></RequireAuth>} />
            {CRM_ENABLED && <Route path="/crm" element={<RequireAuth><CrmPage /></RequireAuth>} />}
            <Route path="/calendar/sp" element={<RequireAuth><CalendarPage /></RequireAuth>} />
            <Route path="/create-service-request" element={<RequireAuth><CreateServiceRequestPage /></RequireAuth>} />
            <Route path="/profile/sp" element={<RequireAuth><SpProfilePage /></RequireAuth>} />
            {/* Standalone editor embedded in the mobile app's WebView — no RequireAuth (auth
                comes from an RN-injected token, not a normal Firebase session) and no chrome
                (see shouldShowHeader/Footer/riaWidget exclusions below). */}
            <Route path="/embed/forms/edit" element={<EmbedFormEditPage />} />
            {/* Public share-download landing page — the "Download Document" email
                button lands here; auto-triggers a Blob download from the API. */}
            <Route path="/share/:token" element={<SharedDocumentDownloadPage />} />
            {/* Public seller-disclosure landing pages — recipient identifies + OTP-verifies
                (no auth). Backend validates the shareToken and the emailed session JWT.
                The leading :addressSlug segment (e.g. /1123-sample-street/{token}) is
                cosmetic — added so the link itself shows the property to the recipient —
                and is never read by the page; only :shareToken (and ?session= for the
                viewer) are used for API calls. Both slugged and un-slugged forms stay
                registered: un-slugged for links emailed before this change, slugged for
                every new one going forward. React Router v6 ranks a static segment (the
                literal "view") above a dynamic one at the same depth, so a pathname like
                /disclosure/{token}/view still resolves to the un-slugged /view route
                below rather than being misread as addressSlug={token}, shareToken="view". */}
            <Route path="/disclosure/:shareToken/view" element={<DisclosureViewPage />} />
            <Route path="/disclosure/:addressSlug/:shareToken/view" element={<DisclosureViewPage />} />
            <Route path="/disclosure/:addressSlug/:shareToken" element={<DisclosureRecipientPage />} />
            <Route path="/disclosure/:shareToken" element={<DisclosureRecipientPage />} />
            {/* Same recipient viewer for arbitrary Transaction Documents folder
                shares — different URL prefix keeps the address bar honest
                (recipient never sees "disclosure" for a non-disclosure folder). */}
            <Route path="/shared/:shareToken/view" element={<DisclosureViewPage />} />
            <Route path="/shared/:addressSlug/:shareToken/view" element={<DisclosureViewPage />} />
            <Route path="/shared/:addressSlug/:shareToken" element={<DisclosureRecipientPage />} />
            <Route path="/shared/:shareToken" element={<DisclosureRecipientPage />} />
            {/* Public offer-upload link flow — buyers / buyer agents / others land
                here from the MLS or a direct share, identify themselves, OTP-verify,
                and are redirected to the session-gated upload page. */}
            <Route path="/offer/:linkId/upload" element={<OfferUploadPage />} />
            <Route path="/offer/:addressSlug/:linkId/upload" element={<OfferUploadPage />} />
            <Route path="/offer/:addressSlug/:linkId" element={<OfferUploadRecipientPage />} />
            <Route path="/offer/:linkId" element={<OfferUploadRecipientPage />} />
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        </main>
        {!isDashboardPage && !isProfilePage && !isConversationPage && !isTransactionsPage && !isDocumentsPage && !isPropertiesPage && !isFavoritesPage && !isListingTransactionPage && !isPurchaseTransactionPage && !isInviteSomeonePage && !isTransactionDetailPage && !isPropertyDetailPage && !isInvitePage && !isNotificationsPage && !isPaymentsPage && !isConnectedServicesPage && !isNotificationSettingsPage && !isSettingsPage && !isActivityLogPage && !isUserActivityPage && !isDocusignCallbackPage && !isDocusignSenderCallbackPage && !isTourPage && !isMFAEnrollmentPage && !isSpDashboardPage && !isSpConversationsPage && !isJobsPage && !isJobDetailPage && !isCalendarPage && !isSpCalendarPage && !isCreateServiceRequestPage && !isSpProfilePage && !isInviteInspectorPage && !isEmbedFormEditPage && !isShareDownloadPage && !isDisclosurePublicPage && !isOfferUploadPublicPage && !isMyNotesPage && !isCrmPage && <Footer />}
        {shouldShowRiaWidget && <FloatingRiaWidget />}
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}


export default App;
